/**
 * Central brand / white-label configuration — the single source of truth for
 * the TodAidesign white-label of TodAidesign.
 *
 * Every brand-, domain-, contact-, and color-specific value flows from `.env`
 * (the `VITE_*` vars), so rebranding is a config change — not a code edit.
 *
 * - The app (Vite / SSG / tests) imports THIS module (`import.meta.env`).
 * - Node/tsx build scripts import the mirror at `scripts/lib/brand.mjs`
 *   (same `VITE_*` vars, read from `process.env`). Keep the fallback defaults
 *   below in sync with that file.
 *
 * Fallbacks are placeholders for local dev only — production must set real
 * values in `.env`. Anything marked TODO(brand) is awaiting the owner's final
 * brand name / domain / legal entity.
 */

const env = import.meta.env;

const stripTrailingSlash = (url: string): string => url.replace(/\/+$/, '');

export const BRAND = {
  /** Public product / brand display name. */
  name: env.VITE_BRAND_NAME ?? 'TodAidesign',
  /** Legal / operating company name. */
  legalName: env.VITE_BRAND_LEGAL_NAME ?? 'TodAidesign',
  /** Canonical production origin, no trailing slash (SEO canonical, hreflang, OG). */
  siteUrl: stripTrailingSlash(env.VITE_SITE_URL ?? env.VITE_APP_URL ?? 'https://todai.example'),
  /** App origin for auth redirects; falls back to siteUrl, then window at runtime. */
  appUrl: stripTrailingSlash(env.VITE_APP_URL ?? env.VITE_SITE_URL ?? 'https://todai.example'),
  contactEmail: env.VITE_CONTACT_EMAIL ?? 'contact@todai.example',
  salesEmail: env.VITE_SALES_EMAIL ?? 'sales@todai.example',
  supportEmail: env.VITE_SUPPORT_EMAIL ?? 'support@todai.example',
  /** X/Twitter handle, including leading @. */
  twitterHandle: env.VITE_TWITTER_HANDLE ?? '@todai',
  /** Prefix for localStorage / cache keys (lowercase, no spaces). */
  storagePrefix: env.VITE_STORAGE_PREFIX ?? 'todai',
  themeColor: env.VITE_THEME_COLOR ?? '#0D47D6',
  backgroundColor: env.VITE_BACKGROUND_COLOR ?? '#0F172A',
} as const;

/** Canonical site origin (no trailing slash). Convenience alias for BRAND.siteUrl. */
export const SITE_URL = BRAND.siteUrl;
